import { useQuery } from '@tanstack/react-query';

import { ImpersonationBroker, type IFormResponse } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from '../data/queryKeys';
import { fromFormResponse, type DealFormConfig } from './types';

export type UseDealFormResult = {
	config: DealFormConfig | undefined;
	isLoading: boolean;
	error: Error | null;
};

/**
 * Fetches the deal-form schema for a lead. When `dealId` is omitted the
 * Create Deal schema is loaded via `GET lead/{companyId}/CreateDeal`; when
 * provided, the Update Deal schema (with current field values) is loaded
 * via `GET lead/{companyId}/UpdateDeal/{dealId}`. Mirrors legacy
 * `CustomFormViewModel.loadForm` (see `aida/viewModels/form.ts:447-469`),
 * which uses the same `baseApiUrl` for both modes — only the URL changes.
 *
 * Disabled when `companyId` is undefined.
 */
export function useDealForm(companyId: string | undefined, dealId?: string): UseDealFormResult {
	const userSession = useUserSession();

	const urlPath = dealId ? `lead/${companyId}/UpdateDeal/${dealId}` : `lead/${companyId}/CreateDeal`;

	const query = useQuery<DealFormConfig, Error>({
		queryKey: queryKeys.focusedView.dealForm(companyId, dealId),
		enabled: !!companyId,
		queryFn: async () => {
			// `callAsync<T>` resolves directly to `T` (and throws on failure) — see
			// `extViewmodels/sdk/WebServiceHelper.ts`. Both the Create and Update
			// Deal endpoints return the `IFormResponse` payload directly, matching
			// the legacy `CustomFormViewModel.loadForm` contract.
			const response = await userSession.webServiceHelper.callAsync<IFormResponse<unknown>>(
				ImpersonationBroker.composeApiUrl({ urlPath }),
				'GET'
			);
			return fromFormResponse(response);
		},
	});

	return {
		config: query.data,
		isLoading: query.isLoading,
		error: query.error ?? null,
	};
}
